<template>
    <div class="select-box"
         :class="{'form-group': isDefault}"
         @keydown="onKeydown">
        <div class="d-flex align-items-center">
            <label v-if="label"
                   :for="id"
            >
                {{ label }}
            </label>
            <div v-if="info"
                 class="with-tooltip"
            >
                <img class="info-tooltip"
                     src="../../assets/img/svg/question.svg"
                >
                <div class="tooltiptext"
                     v-html="info">
                </div>
            </div>
        </div>
        <div v-click-outside="onBlur">
            <input type="text"
                   autocomplete="nope"
                   ref="input"
                   :id="id"
                   :style="[inputStyle, height]"
                   :readonly="multipleSelect"
                   :class="{'focus': isSelectOpen, 'disabled': disabled}"
                   :placeholder="placeholder"
                   :disabled="disabled || !dataList.length"
                   :name="name"
                   v-model="value"
                   v-validate="{required}"
                   @keypress="search"
                   @focus="toggleSelect"
            >
            <i class="material-icons"
               ref="triangle"
               @click="focusInput"
            >
                arrow_drop_down
            </i>
            <div v-if="isSelectOpen && !disabled"
                 class="datalist"
                 ref="datalist"
                 :style="dataListStyle"
            >
                <div v-for="(item, index) of options"
                     :key="index"
                     class="select-item"
                     :title="formatCallback(item)"
                     :class="{'hover': index === keyHoverIndex}"
                     @mouseover="onItemHover(index)"
                     @click="selectItem(item)"
                >
                    <span>{{ formatCallback(item) }}</span>
                </div>
            </div>

            <div class="selected-values"
                 ref="selectedValuesList"
            >
                <chip v-for="(value, index) of selectedValues"
                      class="chip"
                      :key="index"
                      :label="formatCallback(value)"
                      :style="{'width': '210px'}"
                      :required-chip="value.required"
                      :price="value.price"
                      @close="removeDate(index)"
                />
            </div>
        </div>
        <div v-show="field[name] && (field[name].touched ||
                    field[name].validated)"
             class="error">{{ errors.first(name) }}
        </div>
    </div>
</template>

<script>
    import * as _ from 'lodash';
    import ClickOutside from 'vue-click-outside';

    const UP = 38;
    const DOWN = 40;
    const ENTER = 13;
    const TAB = 9;
    const BACKSPACE = 8;

    const CHIP_WIDTH = 210;
    const CHIP_HEIGHT = 32;
    const ICON_WIDTH = 30;
    const GAP = 8;
    const DEFAULT_HEIGHT = 49;

    export default {
        name: 'SelectBox',

        props: {
            dataList: {
                type: Array,
                required: true
            },
            label: {
                type: String,
                required: false
            },
            disabled: {
                type: Boolean,
                default: false
            },
            placeholder: {
                type: String,
                required: false
            },
            id: {
                type: Number,
                required: false
            },
            border: {
                type: String,
                required: false
            },
            borderRadius: {
                type: String,
                required: false
            },
            showError: {
                type: Boolean,
                default: false
            },
            defaultValue: {
                type: [String, Number, Array, Object],
                default: null
            },
            objDataList: {
                type: Array,
                default: null
            },
            width: {
                type: String,
                required: false
            },
            height: {
                type: String,
                required: false,
                default: '49px',
            },
            bottomSpace: {
                type: Boolean,
                required: false
            },
            multipleSelect: {
                type: Boolean,
                default: false
            },
            isDefault: {
                type: Boolean,
                default: true
            },
            formatCallback: {
                type: Function,
                default: el => el
            },
            required: {
                type: Boolean,
                default: true
            },
            info: {
                type: String,
                required: false
            }
        },

        components: {
            Chip: () => import('@/components/controls/Chip')
        },

        directives: { ClickOutside },

        inject: ['$validator'],

        watch: {
            dataList(oldValue, newValue) {
                if (oldValue && newValue && !_.isEqual(oldValue.map(this.formatCallback), newValue.map(this.formatCallback))) {
                    this.options = _.clone(this.dataList);
                    if (this.value && !this.options
                        .map(this.formatCallback)) {
                        this.value = '';
                    }
                    else if (this.options && this.options.length === 1) {
                        this.value = this.formatCallback(this.dataList[0]);
                        if (this.multipleSelect) {
                            this.$emit('selected', this.dataList);
                        }
                        else {
                            this.$emit('selected', this.dataList[0]);
                        }
                    }
                }
            },

            defaultValue: {
                async handler(defaultValue) {
                    await this.$nextTick();
                    if (defaultValue) {
                        if (this.multipleSelect) {
                            if (!_.isEqual(this.selectedValues, defaultValue)) {
                                this.selectedValues = defaultValue;
                                this.value = defaultValue;
                                this.$emit('selected', this.selectedValues);
                            }
                        }
                        else if (!_.isEqual(defaultValue, this.value)) {
                            this.value = this.formatCallback(defaultValue);
                            this.$emit('selected', defaultValue);
                        }
                    }
                    else {
                        if (this.multipleSelect) {
                            this.selectedValues = [];
                            this.value = '';
                            this.$emit('selected', this.selectedValues);
                        }
                        else {
                            this.value = '';
                            this.$emit('selected', this.value);
                        }
                    }
                },
                immediate: true
            },

            selectedValues: {
                async handler(selectedValues) {
                    if (selectedValues) {
                        if (selectedValues.length) {
                            await this.$nextTick();
                            const inputWidth = this.$refs.input.getBoundingClientRect().width;
                            const chipsPerRow = Math.floor((inputWidth - ICON_WIDTH) / CHIP_WIDTH);
                            const rows = Math.ceil(selectedValues.length / chipsPerRow);
                            const height = rows * (CHIP_HEIGHT + GAP) + GAP;
                            this.$refs.input.style.height = height + 'px';
                            this.value = this.formatCallback(selectedValues[0]);
                            if (this.isSelectOpen && !this.disabled) {
                                this.$refs.datalist.style.top = height + 'px';
                            }
                        }
                        else {
                            this.value = '';
                            if (this.isSelectOpen || selectedValues.length === 0) {
                                await this.$nextTick();
                                this.$refs.input.style.height = DEFAULT_HEIGHT + 'px';
                            }
                        }
                        setTimeout(() => {
                            this.options = this.dataList && this.dataList.filter(option => {
                                return !this.selectedValues.map(el => el.id ? el.id : this.formatCallback(el)).includes(option.id ? option.id : this.formatCallback(option));
                            });
                        }, 70);
                    }
                },
                immediate: true
            }
        },

        computed: {
            dataListStyle() {
                if (this.isSelectOpen) {
                    const style = {};
                    const dropdownSpace = 220;
                    const inputSize = 50;
                    if (this.$refs.input.getBoundingClientRect().top + dropdownSpace < window.innerHeight) {
                        if (this.selectedValues && this.selectedValues.length) {
                            style.top = this.$refs.input.style.height
                        } else {
                            style.top = inputSize + 'px';
                        }
                    } else {
                        style.bottom = inputSize + 'px';
                    }
                    return style;
                }
            },

            inputStyle() {
                const style = {};
                if (this.multipleSelect) {
                    style.color = 'white';
                }
                if (this.borderRadius) {
                    style['border-radius'] = this.borderRadius;
                }
                if (this.border) {
                    style.border = this.border;
                }
                if (this.width) {
                    style.width = this.width;
                    style.minWidth = this.width;
                }
                if (this.height) {
                    style.height = this.height;
                }
                return style;
            },

            transform() {
                const style = {
                    transform: `translateY(${this.scrollY}px)`
                };
                return style;
            }
        },

        data() {
            return {
                keyHoverIndex: 0,
                isSelectOpen: false,
                options: [],
                error: false,
                scrollY: 0,
                value: '',
                name: 'select-box-' + Math.random() * 10,
                selectedValues: [],
            }
        },

        methods: {
            toggleSelect() {
                this.isSelectOpen = !this.isSelectOpen;
                if (this.isSelectOpen) {
                    setTimeout(() => {
                        if (this.$refs.datalist) {
                            this.$refs.datalist.scrollTo({
                                top: (this.keyHoverIndex - 3) * 48,
                                left: 0
                            });
                        }
                    }, 300);
                }
            },

            closeSelect(event) {
                setTimeout(() => {
                    if (event) {
                        this.isSelectOpen = false;
                    }
                }, 150);
            },

            onBlur(event) {
                setTimeout(() => {
                    this.closeSelect(event);
                    if (this.value !== '' && this.options && !this.options.map(this.formatCallback).includes(this.value)) {
                        this.value = '';
                        this.$emit('selected', this.value);
                    }
                    this.options = this.options && this.options.filter(option => {
                        return !this.selectedValues.map(el => el.id ? el.id : this.formatCallback(el)).includes(option.id ? option.id : this.formatCallback(option));
                    });
                }, 50);
            },

            selectItem(item) {
                this.errors.remove(this.name);
                this.value = this.formatCallback(item);
                if (this.multipleSelect) {
                    if (!this.selectedValues.includes(item)) {
                        this.selectedValues.push(item);
                    }
                    this.$emit('selected', this.selectedValues);
                }
                else {
                    this.$emit('selected', item);
                    this.closeSelect(item);
                }
            },

            search(event) {
                const value = this.value + event.key;
                const options = this.dataList.map(this.formatCallback).filter(x =>
                    x.toLowerCase().startsWith(value.toLowerCase())
                );
                if (!options.length) {
                    event.preventDefault();
                    this.$emit('');
                } else {
                    this.options = this.dataList.filter(el => options.includes(this.formatCallback(el)));
                    this.$emit('selected', this.options.find(el => this.formatCallback(el) === value));
                }
            },

            focusInput() {
                if (!this.disabled) {
                    this.$refs.input.focus();
                }
            },

            checkForScrollUp() {
                if (this.keyHoverIndex * 48 < this.$refs.datalist.scrollTop) {
                    this.$refs.datalist.scrollIntoView({
                        top: (this.keyHoverIndex) * 48,
                        left: 0,
                        behavior: 'smooth'
                    });
                }
            },

            checkForScrollDown() {
                if (this.keyHoverIndex * 50 - this.$refs.datalist.scrollTop >= 200) {
                    this.$refs.datalist.scrollIntoView({
                        top: (this.keyHoverIndex - 3) * 48,
                        left: 0,
                        behavior: 'smooth'
                    });
                }
            },

            onKeydown(event) {
                switch (event.keyCode) {
                    case UP:
                        if (this.keyHoverIndex > 0) {
                            this.keyHoverIndex--;
                            this.checkForScrollUp();
                        }
                        break;
                    case DOWN:
                        if (this.keyHoverIndex < this.options.length - 1) {
                            this.keyHoverIndex++;
                            this.checkForScrollDown();
                        }
                        break;
                    case ENTER:
                        if (this.keyHoverIndex > -1) {
                            this.selectItem(this.options[this.keyHoverIndex]);
                        }
                        break;
                    case TAB:
                        this.onBlur(event);
                        break;
                    case BACKSPACE:
                        setTimeout(() => {
                            this.value = '';
                            this.options = _.clone(this.dataList);
                        });
                        break;
                }
            },

            onItemHover(index) {
                this.keyHoverIndex = index;
            },

            generateName() {
                this.name = 'name' + Math.random();
            },

            async removeDate(index) {
                const newValues = this.selectedValues.filter((value, i) => i !== index);
                this.selectedValues = [];
                await this.$nextTick();
                this.selectedValues = newValues;
                this.$emit('selected', this.selectedValues);
            },

            clear() {
                this.value = null;
            }
        },

        created() {
            if (this.dataList) {
                this.options = _.clone(this.dataList);
            }
            this.generateName();
        }
    }
</script>

<style lang="scss" scoped>
    @import '../../assets/styles/variables/colors';

    .focus {
        border: 2px solid $Yellow !important;
    }

    .hover {
        background-color: $ComplementaryBackgroundLightGrey;
    }

    .disabled {
        border-radius: 4px;
        border: solid 2px $ComplementaryBgTxtDarker;
        background-color: $ComplementaryBgTxtDarker;
        color: $GreyOpacity;

        &:focus {
            border: 2px solid $ComplementaryBgTxtDarker;
        }
    }

    .select-box {
        position: relative;
        width: 100%;

        > div {
            position: relative;
            width: 100%;
            display: flex;
            align-items: center;

            input {
                width: 100%;
                min-width: 100%;
                border-radius: 4px;
                border: 2px solid $ComplementaryBgTxtDarker;

                &:focus {
                    @extend .focus;
                }
            }

            i {
                position: relative;
                right: 33px;
                cursor: pointer;
                color: $GreySecondary;
            }

            .datalist {
                z-index: 5;
                position: absolute;
                left: 0;
                width: 100%;
                max-height: 200px;
                overflow-y: auto;
                background: $White;
                box-shadow: 0 0 20px 0 rgba(176, 186, 197, 0.15);

                &::-webkit-scrollbar {
                    width: 8px;
                }

                &::-webkit-scrollbar-thumb {
                    border-radius: 4px;
                    background-color: $GreySecondary;
                }

                .select-item {
                    z-index: 5;
                    min-width: 100%;
                    height: 49px;
                    display: flex;
                    align-items: center;
                    padding-left: 16px;
                    color: $Dark;
                    cursor: pointer;

                    &:hover {
                        @extend .hover;
                    }

                    span {
                        overflow: hidden;
                        white-space: nowrap;
                        text-overflow: ellipsis;
                    }
                }
            }
        }
    }

    .selected-values {
        z-index: 3;
        position: absolute;
        top: 8px;
        left: 8px;
        display: flex;
        flex-wrap: wrap;
        margin-right: 30px;

        .chip {
            margin: 0 8px 8px 0;
        }
    }

    .tooltiptext {
        margin-left: 13px;
    }
</style>



// WEBPACK FOOTER //
// src/components/controls/SelectBox.vue